/**
 * Pure helper functions for guide position math used by the ruler/guide
 * drag handlers in main.ts. Extracted here so they can be unit-tested
 * without a DOM.
 *
 * Coordinate conventions (matches src/core/render/Viewport.ts):
 *   panX — screen-space X where the document's left edge (x=0) lands
 *   panY — screen-space Y where the document's top edge (y=0) lands
 *   zoom — 1 === 100%
 *   screenX/Y — pixels relative to the canvas element's top-left corner
 */

/** Slim bounding-rect shape so callers don't need a real DOMRect. */
export interface SimpleRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Convert a client-space event coordinate to a doc-space guide position.
 *
 * @param axis          - "horizontal" → guide is a horizontal line, position is a Y
 *                        doc coordinate; "vertical" → position is an X doc coordinate.
 * @param clientX       - MouseEvent.clientX
 * @param clientY       - MouseEvent.clientY
 * @param canvasRect    - getBoundingClientRect() of the main stage canvas
 * @param panX          - viewport.panX
 * @param panY          - viewport.panY
 * @param zoom          - viewport.zoom
 * @returns doc-space position for the guide
 */
export function screenToGuidePos(
  axis: "horizontal" | "vertical",
  clientX: number,
  clientY: number,
  canvasRect: SimpleRect,
  panX: number,
  panY: number,
  zoom: number,
): number {
  if (axis === "horizontal") {
    // Horizontal guide → position is a Y doc coordinate
    const screenY = clientY - canvasRect.top;
    return (screenY - panY) / zoom;
  } else {
    // Vertical guide → position is an X doc coordinate
    const screenX = clientX - canvasRect.left;
    return (screenX - panX) / zoom;
  }
}

/**
 * Return true when the given client-space pointer lands inside the ruler
 * strip that was the origin of the current drag.
 *
 * The "ruler strip" is the 20px band at the top (for horizontal guides) or
 * left (for vertical guides) of the viewport area described by canvasRect.
 *
 * @param axis       - which guide type / which ruler strip to test
 * @param clientX    - MouseEvent.clientX
 * @param clientY    - MouseEvent.clientY
 * @param canvasRect - getBoundingClientRect() of the main stage canvas
 * @param rulerSize  - width/height of the ruler strip in CSS pixels (default 20)
 */
export function isOverRuler(
  axis: "horizontal" | "vertical",
  clientX: number,
  clientY: number,
  canvasRect: SimpleRect,
  rulerSize = 20,
): boolean {
  if (axis === "horizontal") {
    // Top ruler: a band above the canvas top edge.
    // The ruler sits at [canvasRect.top - rulerSize, canvasRect.top].
    return (
      clientY >= canvasRect.top - rulerSize &&
      clientY < canvasRect.top &&
      clientX >= canvasRect.left &&
      clientX <= canvasRect.right
    );
  } else {
    // Left ruler: a band to the left of the canvas left edge.
    return (
      clientX >= canvasRect.left - rulerSize &&
      clientX < canvasRect.left &&
      clientY >= canvasRect.top &&
      clientY <= canvasRect.bottom
    );
  }
}
